// In-memory PersistencePort. The web viewer has NO offline support and no
// IndexedDB persistence by spec (web-viewer §Behavior: "in-memory docs
// only") — docs live only as long as the tab. This still satisfies the
// DocStore's port so the sync path is identical to the plugin's.

import type { KVStore, PersistencePort, ShadowEntry } from "@cairn/core";

interface StoredDoc {
  updates: Uint8Array[];
  epoch: number;
}

class MemKV implements KVStore {
  private readonly m = new Map<string, string>();
  get(key: string): Promise<string | null> {
    return Promise.resolve(this.m.get(key) ?? null);
  }
  set(key: string, value: string): Promise<void> {
    this.m.set(key, value);
    return Promise.resolve();
  }
  delete(key: string): Promise<void> {
    this.m.delete(key);
    return Promise.resolve();
  }
}

export class MemPersistence implements PersistencePort {
  private readonly docs = new Map<string, StoredDoc>();
  private readonly shadow = new Map<string, ShadowEntry>();
  readonly kv: KVStore = new MemKV();

  loadDoc(docId: string): Promise<{ updates: Uint8Array[]; epoch: number } | null> {
    const d = this.docs.get(docId);
    return Promise.resolve(d ? { updates: [...d.updates], epoch: d.epoch } : null);
  }

  appendUpdate(docId: string, update: Uint8Array): Promise<void> {
    const d = this.docs.get(docId);
    if (d) d.updates.push(update);
    else this.docs.set(docId, { updates: [update], epoch: 0 });
    return Promise.resolve();
  }

  compactDoc(docId: string, state: Uint8Array, epoch: number): Promise<void> {
    this.docs.set(docId, { updates: [state], epoch });
    return Promise.resolve();
  }

  dropDoc(docId: string): Promise<void> {
    this.docs.delete(docId);
    return Promise.resolve();
  }

  getShadow(): Promise<ShadowEntry[]> {
    return Promise.resolve([...this.shadow.values()]);
  }

  putShadow(e: ShadowEntry): Promise<void> {
    this.shadow.set(e.path, e);
    return Promise.resolve();
  }

  deleteShadow(path: string): Promise<void> {
    this.shadow.delete(path);
    return Promise.resolve();
  }
}
